import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus, ExternalLink, X } from "lucide-react";
import { z } from "zod";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useImageBrightness, avatarBgForBrightness } from "@/hooks/useImageBrightness";
import type { Project, SessionUser } from "@axorial/shared";

const filtersSchema = z.object({
  // scope: "mine" filtra a proyectos donde el user actual es miembro;
  // "all" muestra todo (mismo que antes). Default mine — la mayoría
  // del tiempo querés ver lo tuyo.
  scope: z.enum(["mine", "all"]).default("mine"),
  type: z.enum(["all", "client", "internal"]).default("all"),
  status: z.enum(["all", "discovery", "active", "paused", "completed", "archived"]).default("all"),
  health: z.enum(["all", "green", "yellow", "red", "unknown"]).default("all"),
});

type Filters = z.input<typeof filtersSchema>;

export const Route = createFileRoute("/projects/")({
  component: ProjectsPage,
  validateSearch: filtersSchema,
});

const HEALTH_LABEL: Record<string, string> = {
  green: "Saludable",
  yellow: "Atención",
  red: "Riesgo",
  unknown: "—",
};

const STATUS_LABEL: Record<string, string> = {
  discovery: "Discovery",
  active: "Activo",
  paused: "Pausado",
  completed: "Completado",
  archived: "Archivado",
};

type ProjectWithMembers = Project & {
  memberUserIds: string[];
  clientCompanyName: string | null;
};

function ProjectsPage() {
  const filters = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });
  const { data, isLoading } = useQuery<{ projects: ProjectWithMembers[] }>({
    queryKey: ["projects"],
    queryFn: () => api.get("/projects"),
  });

  const setFilter = (key: keyof Filters, value: string) => {
    navigate({
      to: ".",
      search: (prev: Filters) => ({ ...prev, [key]: value }),
      replace: true,
    });
  };

  const clearFilters = () => {
    navigate({
      to: ".",
      // scope se mantiene — limpiar filtros NO te saca de "Mis proyectos".
      search: (prev: Filters) =>
        ({ scope: prev.scope, type: "all", status: "all", health: "all" }) as Filters,
      replace: true,
    });
  };

  const allProjects = data?.projects ?? [];
  const myProjects = me.data
    ? allProjects.filter((p) => p.memberUserIds.includes(me.data!.id))
    : allProjects;
  const scoped = filters.scope === "mine" ? myProjects : allProjects;

  const filtered = scoped.filter((p) => {
    if (filters.type !== "all" && p.type !== filters.type) return false;
    if (filters.status !== "all" && p.status !== filters.status) return false;
    if (filters.health !== "all" && p.health !== filters.health) return false;
    return true;
  });

  const anyFilterActive =
    filters.type !== "all" || filters.status !== "all" || filters.health !== "all";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="proyectos"
        title="Equipo y trabajo en curso"
        subtitle="Cliente o internos. Configura links a Plane, Mural, Drive y notas de cada proyecto."
        actions={
          <Link to="/projects/new" className="btn btn-primary">
            <Plus className="size-4" /> Nuevo proyecto
          </Link>
        }
      />

      {/* Toggle Mis proyectos / Todos: full width, 50/50, segmented
          control con estado activo claramente diferenciado. Es el primer
          decision-point de la página — no debe parecer un filtro más. */}
      <div
        role="tablist"
        className="grid grid-cols-2 gap-0 rounded-xl overflow-hidden border border-base-300/60 bg-base-200/30"
      >
        {(
          [
            { v: "mine", l: "Mis proyectos", count: myProjects.length },
            { v: "all", l: "Todos los proyectos", count: allProjects.length },
          ] as { v: "mine" | "all"; l: string; count: number }[]
        ).map((opt) => {
          const active = filters.scope === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setFilter("scope", opt.v)}
              className={
                "flex items-center justify-center gap-2.5 px-4 py-3.5 text-sm font-semibold transition-all " +
                (active
                  ? "bg-primary text-primary-content shadow-inner"
                  : "text-base-content/60 hover:text-base-content hover:bg-base-200/60")
              }
            >
              {opt.l}
              <span
                className={
                  "text-xs font-bold tabular-nums px-2 py-0.5 rounded-full " +
                  (active
                    ? "bg-primary-content/20 text-primary-content"
                    : "bg-base-300/60 text-base-content/70")
                }
              >
                {opt.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="card-elevated p-3 flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Tipo"
          value={filters.type}
          onChange={(v) => setFilter("type", v)}
          options={[
            { value: "all", label: "Todos" },
            { value: "client", label: "Cliente" },
            { value: "internal", label: "Interno" },
          ]}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(v) => setFilter("status", v)}
          options={[
            { value: "all", label: "Todos" },
            { value: "discovery", label: "Discovery" },
            { value: "active", label: "Activo" },
            { value: "paused", label: "Pausado" },
            { value: "completed", label: "Completado" },
            { value: "archived", label: "Archivado" },
          ]}
        />
        <FilterSelect
          label="Salud"
          value={filters.health}
          onChange={(v) => setFilter("health", v)}
          options={[
            { value: "all", label: "Todas" },
            { value: "green", label: "Saludable" },
            { value: "yellow", label: "Atención" },
            { value: "red", label: "Riesgo" },
            { value: "unknown", label: "Sin definir" },
          ]}
        />
        {anyFilterActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="btn btn-ghost btn-sm gap-1.5 ml-auto"
          >
            <X className="size-3.5" /> Limpiar filtros
          </button>
        )}
        <div className="ml-auto text-sm text-base-content/55 sm:ml-0">
          {data ? `${filtered.length} de ${data.projects.length}` : null}
        </div>
      </div>

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((p) => {
          const coverUrl = p.coverR2Key ? `/api/projects/${p.id}/cover?v=${p.updatedAt}` : null;
          return (
            <Link
              key={p.id}
              to="/projects/$projectId"
              params={{ projectId: p.id }}
              className="card-elevated overflow-hidden group flex flex-col hover:[&]:card-elevated-hover"
            >
              {/* Cover banner */}
              <div className="relative h-24 shrink-0">
                {coverUrl ? (
                  <img src={coverUrl} alt="" className="absolute inset-0 size-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/35 via-secondary/25 to-accent/20" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-base-100/60 to-transparent" />
              </div>

              <div className="p-5 pt-0 flex flex-col gap-3 flex-1">
                {/* Avatar sobrepuesto + meta. La fila va relative z-10 para
                    pintar encima del cover (que es absolute y estaría arriba
                    en painting order si dejáramos la fila como static block). */}
                <div className="relative z-10 -mt-7">
                  <ProjectAvatar project={p} elevated />
                </div>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <span
                      className={`badge badge-sm font-semibold mb-1.5 ${
                        p.type === "client" ? "badge-info" : "badge-ghost"
                      }`}
                    >
                      {p.type === "client" ? "Cliente" : "Interno"}
                    </span>
                    <h3 className="font-display font-semibold text-base-content text-lg leading-snug truncate">
                      {p.name}
                    </h3>
                    {p.type === "client" && p.clientCompanyName && (
                      <div className="text-xs text-base-content/55 mt-0.5 truncate">
                        {p.clientCompanyName}
                      </div>
                    )}
                  </div>
                  <ExternalLink className="size-4 text-base-content/30 group-hover:text-primary transition-colors shrink-0 mt-1" />
                </div>

                <p className="text-sm text-base-content/70 line-clamp-2 leading-relaxed flex-1">
                  {p.description ?? <span className="italic opacity-60">Sin descripción aún.</span>}
                </p>

                <div className="flex items-center justify-between pt-2 border-t border-base-300/50">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-medium capitalize ${
                        p.health === "red"
                          ? "text-error"
                          : p.health === "yellow"
                            ? "text-warning"
                            : p.health === "green"
                              ? "text-success"
                              : "text-base-content/50"
                      }`}
                    >
                      {HEALTH_LABEL[p.health] ?? "—"}
                    </span>
                    <span className="text-base-content/30">·</span>
                    <span className="text-xs text-base-content/60">
                      {STATUS_LABEL[p.status] ?? p.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {p.planeProjectId && (
                      // Shortcut directo a Plane sin abrir el detalle del
                      // proyecto. stopPropagation porque el padre <Link>
                      // navegaría al detalle al click.
                      <a
                        href={`https://tickets.axorial.mx/${p.planeWorkspaceSlug ?? "axorial"}/projects/${p.planeProjectId}/issues`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="badge badge-sm gap-1 font-medium hover:badge-primary transition-colors"
                        title="Abrir en Plane"
                      >
                        Plane <ExternalLink className="size-2.5" />
                      </a>
                    )}
                    {p.mattermostChannelId && (
                      <span className="badge badge-ghost badge-sm font-medium">MM</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
        {data && data.projects.length === 0 && !anyFilterActive && (
          <div className="col-span-full">
            <div className="card-elevated py-16 text-center">
              <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
                <Plus className="size-6 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-base-content mb-1">
                Aún no hay proyectos
              </h3>
              <p className="text-sm text-base-content/60 mb-5">
                Crea el primero para comenzar a trackearlo aquí.
              </p>
              <Link to="/projects/new" className="btn btn-primary">
                Crear el primer proyecto
              </Link>
            </div>
          </div>
        )}
        {data && filtered.length === 0 && data.projects.length > 0 && anyFilterActive && (
          <div className="col-span-full">
            <div className="card-elevated py-12 text-center">
              <p className="text-sm text-base-content/60 mb-3">
                Ningún proyecto coincide con los filtros activos.
              </p>
              <button type="button" onClick={clearFilters} className="btn btn-ghost btn-sm">
                Limpiar filtros
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-xs uppercase tracking-wider text-base-content/55 hidden sm:inline">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="select select-bordered select-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Avatar del proyecto en la card de la lista. Image cuando hay
 * avatarR2Key; fallback a iniciales sobre gradiente brand. Cache-bust
 * por updatedAt para que cambios entren al instante.
 *
 * `elevated`: agrega borde + sombra para verse bien overlapeando el
 * cover banner.
 */
function ProjectAvatar({ project, elevated = false }: { project: Project; elevated?: boolean }) {
  const url = project.avatarR2Key
    ? `/api/projects/${project.id}/avatar?v=${project.updatedAt}`
    : null;
  // Brillo del logo decide el fondo: logo oscuro → bg claro, logo
  // claro → bg oscuro, neutro → gradient con tinte. Sin logo: gradient
  // saturado con las iniciales encima.
  const brightness = useImageBrightness(url);
  const bg = url
    ? avatarBgForBrightness(brightness)
    : "bg-gradient-to-br from-primary/40 to-secondary/40";
  return (
    <div
      className={
        elevated
          ? `relative z-10 size-14 rounded-xl overflow-hidden grid place-items-center shrink-0 border-4 border-base-100 shadow-md ${bg}`
          : `size-11 rounded-lg overflow-hidden grid place-items-center shrink-0 ${bg}`
      }
    >
      {url ? (
        <img src={url} alt="" className="size-full object-contain p-1" />
      ) : (
        <span className="text-white text-sm font-bold tracking-tight">
          {projectInitials(project.name)}
        </span>
      )}
    </div>
  );
}

function projectInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
